import { Box, SxProps, Theme } from '@mui/material';
import { memo, useMemo } from 'react';
import type { ReactNode } from 'react';
import { DragModal, DragModalHeader, FULL_BODY_SX } from 'components/DragModal/DragModal';
import type {
  CoreLogEntry,
  CoreLogQueryField,
} from 'providers/contexts/extensions/CoreLogs';
import type { LogViewerLabels, LogViewerRow } from './LogViewer';
import { LogFilters, LogSearchProvider } from './LogSearchProvider';
import { LogSearchContent } from './LogSearchContent';

/**
 * Log Search Modal
 */

export type LogSearchModalProps = {
  open: boolean;
  onClose: () => void;
  dataTestId?: string;
  /** Modal header title. */
  title: string;
  /** Fixed log name (path param), e.g. `tncms_notifier`. */
  log: string;
  /** Fixed `fields` filter that scopes the log to the caller's record. */
  fixedFields?: CoreLogQueryField;
  /** Initial filters selection. */
  initialFilters?: LogFilters;
  /** Maps a raw log entry to a LogViewer row. */
  toRow: (entry: CoreLogEntry, index: number) => LogViewerRow;
  /** Optional transform of the mapped rows (e.g. chronological ordering). */
  transformRows?: (rows: LogViewerRow[]) => LogViewerRow[];
  /** Show the Driver column in the grid. */
  showDriver?: boolean;
  /** Grid labels (columns / statuses / empty / etc). */
  labels: LogViewerLabels;
  /** Footer content (e.g. close button, status badge). */
  footer?: ReactNode;
  /** Gate the log query (e.g. false until a job id resolves). */
  enabled?: boolean;
  /** When provided, shown instead of the search body while caller data loads. */
  loading?: ReactNode;
  /** When provided, shown instead of the search body for caller-level errors. */
  error?: ReactNode;
};

const EMPTY_FILTERS: LogFilters = {};

export const LogSearchModal = memo(
  ({
    open,
    onClose,
    title,
    log,
    fixedFields,
    initialFilters,
    toRow,
    transformRows,
    showDriver = true,
    labels,
    footer,
    enabled = true,
    loading,
    error,
    dataTestId = 'log-search-modal',
  }: LogSearchModalProps) => {
    return (
      <DragModal
        open={open}
        onClose={onClose}
        initialWidth={1000}
        initialHeight={700}
        minWidth={500}
        minHeight={400}
        isDraggable={true}
        isResizable={true}
        isFullscreenable={true}
        data-testid={dataTestId}
        bodySx={FULL_BODY_SX}
        header={<DragModalHeader text={title} />}
        footer={footer}
        body={
          <LogSearchModalBody
            initialFilters={initialFilters ?? EMPTY_FILTERS}
            log={log}
            fixedFields={fixedFields}
            toRow={toRow}
            transformRows={transformRows}
            showDriver={showDriver}
            labels={labels}
            enabled={enabled}
            loading={loading}
            error={error}
          />
        }
      />
    );
  },
);
LogSearchModal.displayName = 'LogSearchModal';

type LogSearchModalBodyProps = {
  initialFilters: LogFilters;
  log: LogSearchModalProps['log'];
  fixedFields: LogSearchModalProps['fixedFields'];
  toRow: LogSearchModalProps['toRow'];
  transformRows: LogSearchModalProps['transformRows'];
  showDriver: LogSearchModalProps['showDriver'];
  labels: LogSearchModalProps['labels'];
  enabled: LogSearchModalProps['enabled'];
  loading: LogSearchModalProps['loading'];
  error: LogSearchModalProps['error'];
};

const LogSearchModalBody = memo(
  ({
    initialFilters,
    log,
    fixedFields,
    toRow,
    transformRows,
    showDriver,
    labels,
    enabled,
    loading,
    error,
  }: LogSearchModalBodyProps) => {
    const wrapperSx: SxProps<Theme> = useMemo(() => {
      return {
        p: 1,
        width: '100%',
        height: '100%',
      };
    }, []);

    if (loading) {
      return (
        <Box
          data-testid="log-search-modal-loading"
          sx={wrapperSx}
        >
          {loading}
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          data-testid="log-search-modal-error"
          sx={wrapperSx}
        >
          {error}
        </Box>
      );
    }

    return (
      <LogSearchProvider
        initialFilters={initialFilters}
        log={log}
        fixedFields={fixedFields}
        toRow={toRow}
        transformRows={transformRows}
        showDriver={showDriver}
        labels={labels}
        enabled={enabled}
      >
        <LogSearchContent />
      </LogSearchProvider>
    );
  },
);
LogSearchModalBody.displayName = 'LogSearchModalBody';
